<script lang="ts">
    /**
     * CompanyHeroSection - 회사 허브 히어로
     *
     * 회사(주식회사 제이드아이) 미션 소개.
     * 기존 HeroSection 의 jade 그라데이션 + fade-in 패턴 재사용.
     */
    import { getPin } from '../../../../lib/pin';
    import { getInk } from '../../../../lib/ink';
    import { getPop } from '../../../../lib/pop';

    // 📍 꺼진 환경(dev·롤백)에서는 소개 문장의 Pin 절만 빠진다.
    const pin = getPin();
    // 🖋️ Ink 도 같다 — 꺼지면 소개 문장의 Ink 절만 빠진다.
    const ink = getInk();
    // 🧪 Pop 도 같다 — 꺼지면 소개 문장의 Pop 절만 빠진다.
    const pop = getPop();

    let isVisible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            isVisible = true;
        }, 100);
        return () => clearTimeout(t);
    });

    function scrollToProducts(e: MouseEvent) {
        e.preventDefault();
        const el = document.querySelector('#products');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    }
</script>

<section class="company-hero">
    <div class="hero-inner" class:visible={isVisible}>
        <span class="hero-eyebrow">주식회사 제이드아이</span>
        <h1 class="hero-title">
            일상에 모험을 더하는<br />
            기술을 만듭니다
        </h1>
        <!-- 서비스 개수를 세는 표현("두 개의 서비스로")을 쓰지 않는다 — Pin 이 꺼지면 즉시 틀린 문장이
             되고, 카드 수와 어긋나는 순간 홈이 스스로를 반박한다. 절만 붙였다 떼는 구조로 바꿨다. -->
        <p class="hero-subtitle">
            제이드아이는 사람들의 하루를 더 건강하고 즐겁게 만드는 기술을 만듭니다.<br />
            <strong>JadEye</strong>로 건강을, <strong>Gridverse</strong>로 모험을{#if pin}, <strong>Pin</strong>으로 길 위의 정보를{/if}{#if ink}, <strong>Ink</strong>로 이야기를{/if}{#if pop}, <strong>Pop</strong>으로 짧은 놀이를{/if} — 일상에 동기를 더합니다.
        </p>
        <div class="hero-actions">
            <a href="#products" class="hero-cta-primary" onclick={scrollToProducts}>
                서비스 둘러보기
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
                </svg>
            </a>
        </div>
    </div>
</section>

<style>
    .company-hero {
        min-height: 78vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 7rem 1.5rem 3rem;
        background: linear-gradient(135deg, #f0f7f3 0%, #e8f5ee 30%, #fafbfa 100%);
        position: relative;
        overflow: hidden;
    }

    .company-hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -30%;
        width: 80%;
        height: 80%;
        background: radial-gradient(circle, rgba(30, 92, 58, 0.06) 0%, transparent 70%);
        pointer-events: none;
    }

    .hero-inner {
        max-width: 48rem;
        margin: 0 auto;
        width: 100%;
        text-align: center;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .hero-eyebrow {
        display: inline-block;
        font-size: 0.9rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: var(--landing-primary, #1e5c3a);
        background: rgba(30, 92, 58, 0.08);
        padding: 0.4rem 1rem;
        border-radius: 999px;
        margin-bottom: 1.5rem;
    }

    .hero-title {
        font-size: clamp(2.2rem, 5vw, 3.5rem);
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.03em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 1.5rem;
    }

    .hero-subtitle {
        font-size: 1.1rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.8;
        margin: 0 0 2.25rem;
    }

    .hero-subtitle strong {
        color: var(--landing-primary, #1e5c3a);
        font-weight: 600;
    }

    .hero-actions {
        display: flex;
        justify-content: center;
    }

    .hero-cta-primary {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.9rem 1.8rem;
        background: var(--landing-primary, #1e5c3a);
        color: #ffffff;
        font-size: 1rem;
        font-weight: 600;
        text-decoration: none;
        border-radius: 999px;
        box-shadow: 0 8px 24px rgba(30, 92, 58, 0.25);
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
    }

    .hero-cta-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(30, 92, 58, 0.32);
    }

    @media (min-width: 768px) {
        .company-hero {
            padding: 7rem 2.5rem 3rem;
        }
    }

    @media (min-width: 1024px) {
        .company-hero {
            padding: 7rem 3rem 3rem;
        }
    }
</style>
